'use client';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AiStarSmall } from '@/components/assistant-ui/assets/aiStarSmall';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';

import { useSparkJobExplain, type ToolCallInfo } from './useSparkJobExplain';

interface SparkJobExplainSectionProps {
  sparkJobId: string;
  workspaceId: string;
  isTerminal: boolean;
  hasSparkApplicationId: boolean;
}

/** Formats a tool name like "get_application_overview" → "Application Overview" */
function formatToolName(name: string): string {
  return name
    .replace(/^get_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const ToolCallChips = ({ toolCalls }: { toolCalls: ToolCallInfo[] }) => {
  if (toolCalls.length === 0) return null;

  return (
    <Stack direction='row' flexWrap='wrap' gap='6px'>
      {toolCalls.map((tc) => (
        <Chip
          key={tc.toolCallId}
          size='small'
          label={formatToolName(tc.toolName)}
          icon={
            tc.status === 'calling' ? (
              <CircularProgress size={12} />
            ) : (
              <i className='material-symbols-check-circle-outline-rounded h-[16px] w-[16px]' />
            )
          }
          variant='outlined'
          sx={{
            '& .MuiChip-icon': {
              color:
                tc.status === 'done'
                  ? 'var(--mui-palette-success-main)'
                  : undefined,
            },
          }}
        />
      ))}
    </Stack>
  );
};

export const SparkJobExplainSection = ({
  sparkJobId,
  workspaceId,
  isTerminal,
  hasSparkApplicationId,
}: SparkJobExplainSectionProps) => {
  const {
    markdown,
    isStreaming,
    isComplete,
    error,
    toolCalls,
    explain,
    reset,
  } = useSparkJobExplain(workspaceId, sparkJobId);

  if (!isTerminal || !hasSparkApplicationId) return null;

  const hasStarted = isStreaming || isComplete;

  // Idle state — show button only
  if (!hasStarted && !error) {
    return (
      <Stack alignItems='flex-start'>
        <ButtonWithIcon
          variant='outlined'
          text='Explain with AI'
          icon={<AiStarSmall />}
          onClick={explain}
        />
      </Stack>
    );
  }

  // Error state (before any content streamed)
  if (error && !markdown) {
    return (
      <Stack gap='12px'>
        <Alert severity='error' variant='outlined'>
          {error}
        </Alert>
        <Stack direction='row' gap='8px'>
          <ButtonWithIcon
            variant='outlined'
            text='Retry'
            icon='material-symbols-refresh-rounded'
            onClick={() => {
              reset();
              explain();
            }}
          />
        </Stack>
      </Stack>
    );
  }

  // Streaming / Complete state
  return (
    <Stack gap='12px'>
      <Stack direction='row' alignItems='center' gap='8px'>
        <Typography variant='subtitle2' color='text.secondary'>
          AI Explanation
        </Typography>
        {isStreaming && <CircularProgress size={14} />}
      </Stack>

      {toolCalls.length > 0 && <ToolCallChips toolCalls={toolCalls} />}

      {markdown && (
        <Box
          sx={{
            '& h2': {
              fontSize: '0.95rem',
              fontWeight: 600,
              mt: '16px',
              mb: '8px',
              '&:first-of-type': { mt: 0 },
            },
            '& h3': {
              fontSize: '0.875rem',
              fontWeight: 600,
              mt: '12px',
              mb: '4px',
            },
            '& p': {
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mb: '8px',
            },
            '& ul, & ol': {
              fontSize: '0.875rem',
              pl: '20px',
              mb: '8px',
            },
            '& li': {
              mb: '4px',
            },
            '& code': {
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              backgroundColor: 'var(--mui-palette-action-hover)',
              borderRadius: '4px',
              px: '4px',
            },
            '& pre': {
              backgroundColor: 'var(--mui-palette-action-hover)',
              borderRadius: '8px',
              p: '12px',
              overflow: 'auto',
              mb: '8px',
            },
          }}
        >
          <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
        </Box>
      )}

      {error && (
        <Alert severity='warning' variant='outlined'>
          {error}
        </Alert>
      )}

      {isComplete && !isStreaming && (
        <Stack alignItems='flex-start'>
          <ButtonWithIcon
            variant='outlined'
            size='small'
            text='Re-explain'
            icon='material-symbols-refresh-rounded'
            onClick={() => {
              reset();
              explain();
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
